import React, { forwardRef, useEffect, useState } from 'react';

import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';

import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';

import { useEventListFiltersTranslation } from '../../i18n';
import { useEventListFilters } from '../../useEventListFilters';
import st from '../popovers.module.sass';

const validateEventIds = validate.multipleUuid();
const validateCardIds = validate.multipleUuid();

const getIds = (value: string): string[] | undefined => {
  const ids = value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (ids.length === 0) return undefined;
  return ids;
};

function CardIdFilterPopover(
  props: PopoverWithButtonProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { t } = useEventListFiltersTranslation();
  const [eventIds, setEventIds] = useState('');
  const [eventIdsError, setEventIdsError] = useState('');
  const [cardIds, setCardIds] = useState('');
  const [cardIdsError, setCardIdsError] = useState('');
  const { filters, updateFilters, updateFiltersDebounced } = useEventListFilters();

  const handleEventIdsChange = (value: string) => {
    setEventIds(value);

    const validationResult = validateEventIds(value);
    if (typeof validationResult === 'string') {
      setEventIdsError(validationResult);
      return;
    }

    setEventIdsError('');
    updateFiltersDebounced({ event_ids: getIds(value) });
  };

  const handleCardIdsChange = (value: string) => {
    setCardIds(value);

    const validationResult = validateCardIds(value);
    if (typeof validationResult === 'string') {
      setCardIdsError(validationResult);
      return;
    }

    setCardIdsError('');
    updateFiltersDebounced({ card_ids: getIds(value) });
  };

  const reset = () => {
    setEventIds('');
    setEventIdsError('');
    setCardIds('');
    setCardIdsError('');
    updateFilters({ event_ids: undefined, card_ids: undefined });
  };

  const resetEventIds = () => {
    setEventIds('');
    setEventIdsError('');
    updateFilters({ event_ids: undefined });
  };

  const resetCardIds = () => {
    setCardIds('');
    setCardIdsError('');
    updateFilters({ card_ids: undefined });
  };

  useEffect(() => {
    setEventIds(filters.event_ids?.join(',') ?? '');
  }, [filters.event_ids]);

  useEffect(() => {
    setCardIds(filters.card_ids?.join(',') ?? '');
  }, [filters.card_ids]);

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={
        filters.event_ids === undefined
        && filters.card_ids === undefined
      }
      onClickReset={reset}
      ref={ref}
    >
      <div className={st.DateInputWrapper}>
        <Control.Input
          errorMessage={eventIdsError}
          hasError={!!eventIdsError}
          isClearable
          label={t('event_ids')}
          onChangeValue={handleEventIdsChange}
          onClear={resetEventIds}
          value={eventIds}
        />
      </div>
      <div className={st.DateInputWrapper}>
        <Control.Input
          errorMessage={cardIdsError}
          hasError={!!cardIdsError}
          isClearable
          label={t('card_ids')}
          onChangeValue={handleCardIdsChange}
          onClear={resetCardIds}
          value={cardIds}
        />
      </div>
    </PopoverWithButton>
  );
}

const CardIdFilterPopoverWithRef = forwardRef(CardIdFilterPopover);
export { CardIdFilterPopoverWithRef as CardIdFilterPopover };
